import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from '../BaseTool';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { Element } from '../../core/types';
import i18n from '../../i18n';

/**
 * Parking Component: click two corners to lay out a row of parking bays on the
 * terrain. Bay count, stripes and module sizes are stored in geometryData and
 * drawn by ParkingAreaRenderer (white bay lines + yellow access edge).
 */
export class ParkingComponentTool extends BaseTool {
    id = 'SITE_PARKING';
    name = 'Parking Component';
    cursor = 'crosshair';

    private firstPoint: THREE.Vector3 | null = null;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.parkingClickTwoCornersToLay'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        if (!this.firstPoint) {
            this.firstPoint = snapped;
            return;
        }
        this.createParkingRow(snapped);
        this.firstPoint = null;
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        if (this.firstPoint) {
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: rectPreviewPoints(this.firstPoint, snapped),
                closed: true,
                color: '#ffd95e',
            });
        } else {
            this.context.appStore.setToolPreview({
                type: 'point',
                position: snapped,
                color: '#ffd95e',
            });
        }
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape' && this.firstPoint) {
            this.firstPoint = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }

    private createParkingRow(corner: THREE.Vector3) {
        if (!this.context || !this.firstPoint) return;
        const a = this.firstPoint;
        const x0 = Math.min(a.x, corner.x);
        const x1 = Math.max(a.x, corner.x);
        const y0 = Math.min(a.y, corner.y);
        const y1 = Math.max(a.y, corner.y);
        const width = x1 - x0;
        const depth = y1 - y0;
        if (width < 0.5 || depth < 0.5) {
            this.notify(i18n.t('tools.notify.theParkingAreaIsTooSmall'), 'warning');
            this.context.appStore.setToolPreview(null);
            return;
        }
        const props = this.context.bimStore.toolProperties;
        const bayWidth = props.parkingSpaceWidth ?? 2.5;
        const bayDepth = Math.min(props.parkingSpaceDepth ?? depth, depth);
        const bays = Math.max(1, Math.floor(width / bayWidth));
        const elevation = (a.z + corner.z) / 2;

        const parking: Element = {
            id: uuidv4(),
            category: 'Parking Areas',
            type: 'Parking: 90-degree',
            name: 'Parking Row',
            parameters: {
                'Bays': String(bays),
                'Bay Width': `${bayWidth.toFixed(2)} m`,
                'Bay Depth': `${bayDepth.toFixed(2)} m`,
                'Elevation': `${elevation.toFixed(2)} m`,
            },
            constraints: [],
            geometryData: { x0, y0, width, depth, bays, bayWidth, bayDepth, elevation },
        };
        this.context.bimStore.addElement(parking);
        this.notify(i18n.t('tools.notify.parkingRowPlaced', { count: bays }), 'success');
        this.context.appStore.setToolPreview(null);
    }

    onPointerUp(): void { }

    deactivate(): void {
        this.firstPoint = null;
        this.context?.appStore.setToolPreview(null);
        super.deactivate();
    }
}

function rectPreviewPoints(a: THREE.Vector3, b: THREE.Vector3): THREE.Vector3[] {
    const x0 = Math.min(a.x, b.x);
    const x1 = Math.max(a.x, b.x);
    const y0 = Math.min(a.y, b.y);
    const y1 = Math.max(a.y, b.y);
    const z = (a.z + b.z) / 2;
    return [
        new THREE.Vector3(x0, y0, z),
        new THREE.Vector3(x1, y0, z),
        new THREE.Vector3(x1, y1, z),
        new THREE.Vector3(x0, y1, z),
    ];
}
